// NOTE: These constants serve as fallback defaults.
// The authoritative values come from SiteConfig (DB) via getSiteConfig() in src/lib/site-config.ts.
// Server components and API routes should prefer getSiteConfig(). These remain for client components
// and as build-time defaults that won't break if the DB is unavailable.

export const SITE_NAME = "Rivky Wigs";
export const SITE_DESCRIPTION = "Premium handcrafted wigs and accessories";

export const NAV_LINKS = [
  { label: "Home", tKey: "home", href: "/" },
  {
    label: "Wigs",
    tKey: "wigs",
    href: "/wigs",
    children: [
      { label: "All Wigs", tKey: "allWigs", href: "/wigs" },
      { label: "Virgin Hair Collection", tKey: "virginHair", href: "/wigs?tier=virgin" },
      { label: "Processed Hair Collection", tKey: "processedHair", href: "/wigs?tier=processed" },
      { label: "16\"", tKey: "length16", href: "/wigs?length=16" },
      { label: "20\"", tKey: "length20", href: "/wigs?length=20" },
      { label: "24\"", tKey: "length24", href: "/wigs?length=24" },
      { label: "30\"", tKey: "length30", href: "/wigs?length=30" },
      { label: "32\"", tKey: "length32", href: "/wigs?length=32" },
      { label: "Find Your Perfect Wig", tKey: "wigQuiz", href: "/quiz" },
    ],
  },
  {
    label: "Accessories",
    tKey: "accessories",
    href: "/accessories",
    children: [
      { label: "All Accessories", tKey: "allAccessories", href: "/accessories" },
      { label: "Care Products", tKey: "careProducts", href: "/accessories?type=care" },
      { label: "Tools", tKey: "tools", href: "/accessories?type=tools" },
    ],
  },
  { label: "Build Your Wig", tKey: "buildYourWig", href: "/builder" },
  // Services submenu — every entry routes to the unified /book entry
  // with the matching service pre-selected via ?service=. No more
  // split "/consultation" vs "/account/service-pickup" forks in the
  // customer's head; /book reads the query and decides which backend
  // based on whether the picked service needs a drop-off or in-person
  // visit. The top-level "Services" link goes to bare /book.
  {
    label: "Services",
    tKey: "services",
    href: "/book",
    children: [
      { label: "Book Appointment", tKey: "consultation", href: "/book?service=new_wig" },
      { label: "Wash & Set", tKey: "washAndSet", href: "/book?service=wash_set" },
      { label: "Wig Repair", tKey: "wigRepair", href: "/book?service=repair" },
      { label: "Coloring", tKey: "coloring", href: "/book?service=color" },
      { label: "Restyle", tKey: "restyle", href: "/book?service=restyle" },
      { label: "Deep Clean", tKey: "deepClean", href: "/book?service=deep_clean" },
      { label: "Schedule a Pickup", tKey: "schedulePickup", href: "/book" },
    ],
  },
  { label: "Blog", tKey: "blog", href: "/blog" },
  { label: "About", tKey: "about", href: "/about" },
];

// DEPRECATED — replaced by SiteConfig.comingSoon.builder which is
// admin-toggleable in /admin/settings → Coming Soon. Kept as a thin
// shim equal to the historical "true" so any consumers that haven't
// migrated to useSiteConfig() yet still hide builder links by default.
// Remove once every consumer has migrated.
export const BUILDER_COMING_SOON = true;

export const BUILDER_STEPS = [
  { key: "hair_type", label: "Hair Type", description: "Choose your hair quality" },
  { key: "base_type", label: "Base Type", description: "Choose your wig base" },
  { key: "length", label: "Length", description: "Pick your length" },
  { key: "texture", label: "Texture", description: "Select hair texture" },
  { key: "color", label: "Color", description: "Choose your shade" },
  { key: "cap_size", label: "Cap Size", description: "Select your fit" },
  { key: "extras", label: "Extras", description: "Add finishing touches" },
] as const;

// Base price is 0 — the hair_type step sets the starting price (Virgin $5000 or Processed $2800)
export const BUILDER_BASE_PRICE = 0;

// Features included in ALL custom wigs (no extra charge)
export const BUILDER_INCLUDED_FEATURES = [
  "Bleached Knots",
  "Baby Hairs",
  "Pre-Plucked Hairline",
];

// NJ sales tax on clothing over $110 (6.625%)
export const NJ_TAX_RATE = 0.06625;

// US states for checkout state dropdown (50 states, 2-letter codes)
export const US_STATES: { code: string; name: string }[] = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
];

// Service pickup (wig repair) deposit. Set to 0 = no-deposit flow
// (pickup submits without a Stripe charge). Railway env var wins so
// Rivky can toggle on/off without a redeploy. Set 2026-04-14 to 0
// for soft-launch window — raise back to 50 once the flow proves out.
export const SERVICE_DEPOSIT_AMOUNT = parseInt(
  process.env["SERVICE_DEPOSIT_AMOUNT"] || "0",
  10
);

/**
 * Per-appointment-type deposit pricing (2026-04-15 go-live).
 *
 * **LIVE AS OF GO-LIVE.** Deposits are collected at booking via Stripe.
 * The owner flipped this on 2026-04-15 with the rule: **only new-wig
 * consultations charge a deposit**. Everything else (wash & set, color,
 * repair, restyle, keratin) is free at booking time. Platinum tier
 * customers (10k+ lifetime points or admin-set `loyaltyTierOverride`)
 * have ALL deposits waived — see `shouldWaiveDeposit()` in lib/loyalty.
 *
 * Env-var controls still honored:
 *   - APPOINTMENT_DEPOSITS_ENABLED=0 or =false → turn OFF globally
 *   - APPOINTMENT_DEPOSIT_<TYPE>=NN → override any single type's amount
 *     (e.g. APPOINTMENT_DEPOSIT_WASH_SET=20 to start charging wash-and-set)
 */
const _depositsEnvValue = process.env["APPOINTMENT_DEPOSITS_ENABLED"];
export const APPOINTMENT_DEPOSITS_ENABLED =
  _depositsEnvValue === "0" || _depositsEnvValue === "false" ? false : true;

const depositEnv = (key: string, fallback: number): number => {
  const v = process.env[`APPOINTMENT_DEPOSIT_${key}`];
  if (!v) return fallback;
  const n = parseInt(v, 10);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
};

export const APPOINTMENT_DEPOSITS: Record<string, number> = {
  // Free-at-booking types (2026-04-15 owner decision).
  // Rivky wants wash-and-set and other routine services to stay free
  // at the booking step — no deposit friction for existing customers.
  wash_set: depositEnv("WASH_SET", 0),
  color: depositEnv("COLOR", 0),
  repair: depositEnv("REPAIR", 0),
  restyle: depositEnv("RESTYLE", 0),
  deep_clean: depositEnv("DEEP_CLEAN", 0),
  phone: 0,
  other: 0,
  // New-wig consultations: $150 deposit. Platinum waives via
  // shouldWaiveDeposit(). `new_wig` is the dedicated type; `in_store`
  // and `virtual` consultations are also treated as new-wig flows
  // since they're the primary path to a new-wig purchase.
  new_wig: depositEnv("NEW_WIG", 150),
  in_store: depositEnv("IN_STORE", 150),
  virtual: depositEnv("VIRTUAL", 150),
};

/** Return the deposit dollar amount for a given appointment type. */
export function getAppointmentDeposit(type: string): number {
  if (!APPOINTMENT_DEPOSITS_ENABLED) return 0;
  return APPOINTMENT_DEPOSITS[type] ?? 0;
}

/**
 * Per-appointment-type duration in minutes. Rivky 2026-04-15:
 *   - Wash & set: 15 min (quick drop-in / pickup)
 *   - Color, cut, keratin: 30–45 min
 *   - New wig: 45 min (longer if includes cut)
 * Falls back to `AppointmentSettings.slotDurationMinutes` when the
 * type is unknown — keeps the global config as the safety net.
 */
export const APPOINTMENT_DURATIONS: Record<string, number> = {
  wash_set: 15,
  restyle: 30,
  color: 45,
  deep_clean: 45, // Keratin treatment
  new_wig: 45,
  in_store: 30,
  virtual: 30,
  phone: 15,
  repair: 30,
  other: 30,
};

export function getAppointmentDuration(type: string, fallback = 30): number {
  return APPOINTMENT_DURATIONS[type] ?? fallback;
}

/**
 * Resolve the slot duration for a type with settings-level overrides
 * layered on top of the hardcoded defaults. Call sites that have
 * AppointmentSettings handy should prefer this so admin edits in
 * /admin/appointments → Settings actually take effect.
 */
export function resolveTypeDuration(
  type: string,
  settingsTypeDurations: Record<string, number> | null | undefined,
  globalFallback: number
): number {
  const settingsVal = settingsTypeDurations?.[type];
  if (typeof settingsVal === "number" && settingsVal > 0) return settingsVal;
  return APPOINTMENT_DURATIONS[type] ?? globalFallback;
}

// Audit Section 5 polish — Single source of truth for shipping figures.
// Previously hardcoded as `subtotal >= 500 ? 0 : 15.0` in 5 files
// (cart, checkout client, CartDrawer, /api/checkout, /api/orders).
export const FREE_SHIPPING_THRESHOLD = 500; // dollars
export const STANDARD_SHIPPING_RATE = 15.0;
export const UBER_SHIPPING_FALLBACK = 12.99; // when live quote unavailable

// Store address for Uber Direct deliveries
export const STORE_ADDRESS = process.env.NEXT_PUBLIC_STORE_ADDRESS || "1935 Whitesville Rd, Toms River, NJ 08755";

// Contact info
export const SUPPORT_PHONE = process.env.NEXT_PUBLIC_SUPPORT_PHONE || "(347) 452-3279";
export const SUPPORT_EMAIL = process.env.NEXT_PUBLIC_SUPPORT_EMAIL || "info@rivkywigs.com";

// ─── Canonical Status Colors ───
// Use these everywhere to ensure consistent badge styling across admin & customer pages.

/** Order status badge colors (bg + text + border/ring) */
export const ORDER_STATUS_COLORS: Record<string, string> = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  confirmed: "bg-blue-50 text-blue-700 border-blue-200",
  processing: "bg-indigo-50 text-indigo-700 border-indigo-200",
  ready_for_pickup: "bg-purple-50 text-purple-700 border-purple-200",
  out_for_delivery: "bg-cyan-50 text-cyan-700 border-cyan-200",
  shipped: "bg-cyan-50 text-cyan-700 border-cyan-200",
  delivered: "bg-emerald-50 text-emerald-700 border-emerald-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
  refunded: "bg-gray-50 text-gray-700 border-gray-200",
  partially_refunded: "bg-orange-50 text-orange-700 border-orange-200",
};

/** Order status dot colors (for inline indicators) */
export const ORDER_STATUS_DOT_COLORS: Record<string, string> = {
  pending: "bg-amber-500",
  confirmed: "bg-blue-500",
  processing: "bg-indigo-500",
  ready_for_pickup: "bg-purple-500",
  out_for_delivery: "bg-cyan-500",
  shipped: "bg-cyan-500",
  delivered: "bg-emerald-500",
  completed: "bg-emerald-500",
  cancelled: "bg-red-500",
  refunded: "bg-gray-400",
  partially_refunded: "bg-orange-500",
};

/** Appointment status badge colors (bg + text + border) */
export const APPOINTMENT_STATUS_COLORS: Record<string, { bg: string; text: string; dot: string }> = {
  pending:     { bg: "bg-amber-50",   text: "text-amber-700",   dot: "bg-amber-400" },
  confirmed:   { bg: "bg-blue-50",    text: "text-blue-700",    dot: "bg-blue-400" },
  in_progress: { bg: "bg-indigo-50",  text: "text-indigo-700",  dot: "bg-indigo-400" },
  completed:   { bg: "bg-emerald-50", text: "text-emerald-700", dot: "bg-emerald-400" },
  cancelled:   { bg: "bg-red-50",     text: "text-red-700",     dot: "bg-red-400" },
  no_show:     { bg: "bg-gray-50",    text: "text-gray-600",    dot: "bg-gray-400" },
};

/** Service pickup status badge colors (bg + text + border) */
export const SERVICE_PICKUP_STATUS_COLORS: Record<string, string> = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  confirmed: "bg-blue-50 text-blue-700 border-blue-200",
  courier_assigned: "bg-purple-50 text-purple-700 border-purple-200",
  picked_up: "bg-indigo-50 text-indigo-700 border-indigo-200",
  servicing: "bg-orange-50 text-orange-700 border-orange-200",
  ready: "bg-emerald-50 text-emerald-700 border-emerald-200",
  returning: "bg-cyan-50 text-cyan-700 border-cyan-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
};
